import axios from 'axios';
import * as React from 'react';

import wordpressData from 'data';

export interface TempControlProductCard {
  sku: string;
  name: string;
  slug: string;
  permalink: string;
  featured_image: string;
  description: string;
  features: string[];
  spec_sheet_url: string | null;
  installation_guide_url: string | null;
}

type ProductCardsResponse = Record<string, TempControlProductCard | null>;

const fetchProductCards = async (skus: string[]): Promise<ProductCardsResponse> => {
  const uniqueSkus = Array.from(new Set(skus.filter(Boolean)));
  if (!uniqueSkus.length) {
    return {};
  }

  const response = await axios.get<ProductCardsResponse>(wordpressData.ajaxUrl, {
    params: {
      action: 'symmons_tempcontrol_product_cards_by_sku',
      sku: uniqueSkus,
    },
  });

  return response.data ?? {};
};

/**
 * Hydrate one or more catalog SKUs with the same product-card payload
 * the product detail page uses (image, feature bullets, spec / install URLs).
 */
export const useTempControlProductCards = (skus: string[]) => {
  const [cards, setCards] = React.useState<Record<string, TempControlProductCard | null>>({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const skuKey = Array.from(new Set(skus.filter(Boolean)))
    .sort()
    .join('|');

  React.useEffect(() => {
    if (!skuKey) {
      setCards({});
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchProductCards(skuKey.split('|'))
      .then((data) => {
        if (!cancelled) {
          setCards(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Unable to load product details right now.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [skuKey]);

  return { cards, isLoading, error };
};
